/* ==========================================================================
   X-HEALTHY — predictive-search.js
   <predictive-search>: live results through the Predictive Search API,
   trending + recent searches on focus, full keyboard support (combobox).
   ========================================================================== */
if (!customElements.get('predictive-search')) {
  const RECENT_KEY = 'xh-recent-searches';
  const RECENT_MAX = 6;

  const readRecent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
    } catch {
      return [];
    }
  };
  const writeRecent = (list) => {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch {
      /* ignore */
    }
  };
  const saveRecent = (term) => {
    const clean = term.trim();
    if (clean.length < 2) return;
    writeRecent([clean, ...readRecent().filter((item) => item.toLowerCase() !== clean.toLowerCase())]);
  };

  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      // Arabic is not supported by the Predictive Search API: go straight to the fallback
      static useFallback = document.documentElement.lang.toLowerCase().startsWith('ar');

      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[type="search"]');
        this.panel = this.querySelector('.search__panel');
        this.results = this.querySelector('[data-search-results]');
        this.defaults = this.querySelector('[data-search-default]');
        this.resetButton = this.querySelector('[type="reset"]');
        this.cache = new Map();
        this.activeIndex = -1;

        this.input.addEventListener('input', window.theme.utils.debounce(() => this.onInput(), 220));
        this.input.addEventListener('focus', () => this.open());
        this.input.addEventListener('keydown', (event) => this.onKeydown(event));
        this.form.addEventListener('submit', () => saveRecent(this.input.value));
        this.form.addEventListener('reset', () => {
          setTimeout(() => {
            this.onInput();
            this.input.focus();
          });
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.close();
        });
        this.panel.addEventListener('click', (event) => this.onPanelClick(event));
        this.querySelector('[data-recent-clear]')?.addEventListener('click', () => {
          writeRecent([]);
          this.renderRecent();
          this.input.focus();
        });
        document.addEventListener('pointerdown', (event) => {
          if (!this.contains(event.target)) this.close();
        });
      }

      get term() {
        return this.input.value.trim();
      }

      open() {
        this.renderRecent();
        this.showDefaults(!this.term);
        if (!this.term && !this.hasDefaults()) return;
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.setAttribute('open', '');
      }

      close() {
        this.panel.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.removeAttribute('open');
        this.activeIndex = -1;
      }

      hasDefaults() {
        return [...this.defaults.querySelectorAll('.search__group')].some((group) => !group.hidden);
      }

      showDefaults(show) {
        this.defaults.hidden = !show;
        this.results.hidden = show;
      }

      renderRecent() {
        const group = this.querySelector('[data-recent]');
        const list = this.querySelector('[data-recent-list]');
        if (!group || !list) return;
        const recent = readRecent();
        group.hidden = recent.length === 0;
        list.replaceChildren(
          ...recent.map((term, index) => {
            const li = document.createElement('li');
            const link = document.createElement('a');
            link.className = 'search__chip';
            link.setAttribute('role', 'option');
            link.id = `${this.input.id}-recent-${index}`;
            link.href = `${window.theme.routes.search}?q=${encodeURIComponent(term)}&type=product`;
            link.dataset.term = term;
            link.textContent = term;
            li.appendChild(link);
            return li;
          })
        );
      }

      async onInput() {
        const term = this.term;
        this.resetButton.hidden = !term;
        this.activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');

        if (!term) {
          this.open();
          return;
        }

        this.showDefaults(false);
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.setAttribute('open', '');

        if (this.cache.has(term)) {
          this.renderResults(this.cache.get(term));
          return;
        }

        this.controller?.abort();
        this.controller = new AbortController();
        this.setAttribute('loading', '');

        try {
          let response = await fetch(this.buildUrl(term, PredictiveSearch.useFallback), {
            signal: this.controller.signal,
          });
          // The Predictive Search API rejects some languages (Arabic: 417 "Unsupported buyer locale").
          // Switch to the regular search page rendered through the same section.
          if (!response.ok && !PredictiveSearch.useFallback && [417, 422].includes(response.status)) {
            PredictiveSearch.useFallback = true;
            response = await fetch(this.buildUrl(term, true), { signal: this.controller.signal });
          }
          if (!response.ok) throw new Error(response.status);
          const html = new DOMParser()
            .parseFromString(await response.text(), 'text/html')
            .querySelector('#PredictiveSearchResults');
          const markup = html ? html.outerHTML : '';
          this.cache.set(term, markup);
          if (term === this.term) this.renderResults(markup);
        } catch (error) {
          if (error.name !== 'AbortError') this.close();
        } finally {
          this.removeAttribute('loading');
        }
      }

      buildUrl(term, fallback) {
        if (fallback) {
          const params = new URLSearchParams({
            q: term,
            type: 'product,article,page',
            'options[prefix]': 'last',
            'options[unavailable_products]': 'last',
            section_id: this.dataset.section,
          });
          return `${window.theme.routes.search}?${params}`;
        }
        const params = new URLSearchParams({
          q: term,
          section_id: this.dataset.section,
          'resources[type]': 'product,collection,query,article,page',
          'resources[limit]': '8',
          'resources[limit_scope]': 'each',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        });
        return `${window.theme.routes.predictiveSearch}?${params}`;
      }

      renderResults(markup) {
        // Prefix option ids per instance so desktop/mobile copies never collide
        this.results.innerHTML = markup.replaceAll('id="Ps', `id="${this.input.id}-Ps`).replaceAll('aria-labelledby="Ps', `aria-labelledby="${this.input.id}-Ps`);
        this.results.hidden = false;
        const total = Number(this.results.firstElementChild?.dataset.total || 0);
        window.theme.utils.announce(window.theme.strings.searchResults.replace('[count]', total));
      }

      get options() {
        const scope = this.results.hidden ? this.defaults : this.results;
        return [...scope.querySelectorAll('[role="option"]')].filter((el) => el.offsetParent !== null);
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          if (!this.panel.hidden) {
            event.preventDefault();
            this.close();
          }
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
        if (this.panel.hidden) {
          if (event.key === 'ArrowDown') this.open();
          return;
        }
        const options = this.options;
        if (event.key === 'Enter') {
          const active = options[this.activeIndex];
          if (active) {
            event.preventDefault();
            active.click();
          }
          return;
        }
        if (!options.length) return;
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + delta + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
        const active = options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }

      onPanelClick(event) {
        const term = event.target.closest('[data-term]')?.dataset.term;
        if (term) saveRecent(term);
        else if (event.target.closest('a[href]')) saveRecent(this.term);
        if (event.target.closest('[data-search-all]')) {
          event.preventDefault();
          saveRecent(this.term);
          this.form.submit();
        }
      }
    }
  );
}
